import { useState } from "react";
import { PortalChrome, HealthPill, StatusPill, MOCK, useRole } from "./_shared";
import { FileSignature, Package, Inbox, Activity, ChevronRight, Search, Filter, Lock, AlertTriangle, Sparkles } from "lucide-react";

// Option 4 — "Workspace split" (Linear + Front + Intercom inspired)
// Two-column: LEFT = list of partners (with health/attention). RIGHT = selected
// partner detail with 4 inner tabs. Everything is scoped to a relationship.
// Best mirrors your core mental model: "verqo is the shared source of truth
// between two accounts."

const DETAIL_TABS = [
  { id: "overview",  label: "Overview",  icon: Sparkles },
  { id: "agreement", label: "Agreement", icon: FileSignature },
  { id: "orders",    label: "Orders",    icon: Inbox },
  { id: "catalog",   label: "Catalog",   icon: Package },
  { id: "messages",  label: "Messages",  icon: Activity },
];

export default function Option4_Workspace() {
  const [role, setRole] = useRole();
  const m = MOCK[role];
  const isSeller = role === "seller";
  const [selectedId, setSelectedId] = useState(m.partners[0]?.id);
  const [innerTab, setInnerTab] = useState("overview");

  const selected = m.partners.find(p => p.id === selectedId) || m.partners[0];

  return (
    <PortalChrome role={role} setRole={setRole} optionLabel="Option 4 · Workspace split (relationship-first)" optionNumber="4">
      <div className="border-b border-zinc-200 bg-white px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Dropship</h1>
            <p className="text-xs text-zinc-500 mt-0.5">One workspace per {m.partnerLabel.toLowerCase()} relationship</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="text-xs font-medium px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">
              {isSeller ? "Invite buyer" : "Find supplier"}
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* LEFT: partner list */}
        <aside className="w-80 flex-shrink-0 border-r border-zinc-200 bg-white flex flex-col">
          <div className="p-4 border-b border-zinc-100">
            <div className="flex items-center gap-2 bg-zinc-50 ring-1 ring-zinc-200 rounded-md px-2.5 py-1.5">
              <Search className="w-3.5 h-3.5 text-zinc-400" />
              <input placeholder={`Search ${m.partnersLabel.toLowerCase()}…`} className="text-xs bg-transparent flex-1 outline-none" />
              <Filter className="w-3.5 h-3.5 text-zinc-400" />
            </div>
            <div className="flex items-center gap-1 mt-3">
              <FilterChip label="All"      count={m.partners.length} active />
              <FilterChip label="Needs me" count={2} tone="amber" />
              <FilterChip label="Live"     count={m.partners.filter(p => p.health === "ok").length} />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {m.partners.map(p => {
              const isSelected = selectedId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setSelectedId(p.id)}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50/60 transition ${isSelected ? "bg-zinc-900/[0.03] border-l-2 border-l-zinc-900" : "border-l-2 border-l-transparent"}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-xs font-semibold text-zinc-700">
                      {p.name.split(" ").map(w => w[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        {!p.mapOk && <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-zinc-500 tabular-nums">${p.gmv30d.toLocaleString()}/30d</span>
                        <span className="text-zinc-300">·</span>
                        <HealthPill kind={p.health}>{p.sync}</HealthPill>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Bottom action strip */}
          <div className="p-3 border-t border-zinc-100 text-[11px] text-zinc-500 flex items-center justify-between">
            <span>{m.partners.length} {m.partnersLabel.toLowerCase()}</span>
            <span className="tabular-nums">${m.partners.reduce((s, p) => s + p.gmv30d, 0).toLocaleString()} · 30d</span>
          </div>
        </aside>

        {/* RIGHT: relationship detail */}
        <main className="flex-1 overflow-y-auto bg-zinc-50">
          {/* Detail header */}
          <div className="bg-white border-b border-zinc-200 px-8 py-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs text-zinc-500 mb-1">{m.partnerLabel} relationship</div>
                <h2 className="text-2xl font-semibold tracking-tight">{selected.name}</h2>
                <div className="flex items-center gap-2 mt-2">
                  <HealthPill kind={selected.health}>Synced · {selected.sync}</HealthPill>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-zinc-100 inline-flex items-center gap-1"><Lock className="w-3 h-3" />Agreement locked</span>
                  {isSeller && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-zinc-100">{selected.channel}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="text-xs font-medium px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50">Message</button>
                <button className="text-xs font-medium px-3 py-1.5 rounded-md bg-zinc-900 text-white">Force resync</button>
              </div>
            </div>

            {/* KPI mini strip inside header */}
            <div className="grid grid-cols-4 gap-6 mt-5">
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Orders 30d</div><div className="text-lg font-semibold tabular-nums mt-0.5">{selected.orders30d}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">GMV 30d</div>   <div className="text-lg font-semibold tabular-nums mt-0.5">${selected.gmv30d.toLocaleString()}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">Credit</div>    <div className="text-lg font-semibold tabular-nums mt-0.5">{selected.credit}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-zinc-500">MAP</div>       <div className="text-lg font-semibold mt-0.5">{selected.mapOk ? <span className="text-emerald-700">Clean</span> : <span className="text-amber-600">1 flag</span>}</div></div>
            </div>

            {/* Inner tabs */}
            <div className="flex gap-1 mt-5 -mb-5 pb-0">
              {DETAIL_TABS.map(t => {
                const Icon = t.icon;
                const isActive = innerTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setInnerTab(t.id)}
                    className={`text-[13px] font-medium px-3 py-2.5 border-b-2 transition inline-flex items-center gap-1.5 ${isActive ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail content */}
          <div className="p-8 space-y-4">
            {innerTab === "overview" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white border border-zinc-200/80 p-5">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Recent activity</div>
                  <div className="space-y-3">
                    <ActivityRow color="emerald" title="Invoice $211 debited" desc="12m ago" />
                    <ActivityRow color="blue"    title="Tracking pushed to channel" desc="1h ago" />
                    <ActivityRow color="amber"   title="MAP violation flagged" desc="2h ago" />
                    <ActivityRow color="zinc"    title="New order DS-21041" desc="3h ago" />
                  </div>
                </div>
                <div className="rounded-2xl bg-white border border-zinc-200/80 p-5">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Next actions</div>
                  <div className="space-y-2">
                    <ActionRow tone="amber" title="Add tracking to DS-21041" body="SLA 2h left" />
                    <ActionRow tone="zinc"  title="Review Q1 sales report" body="Ready to view" />
                  </div>
                </div>
              </div>
            )}

            {innerTab === "agreement" && (
              <div className="rounded-2xl bg-white border border-zinc-200/80 p-6">
                <div className="text-sm font-semibold mb-4">Locked terms</div>
                <div className="grid grid-cols-2 gap-y-3 text-sm">
                  <div className="text-zinc-500">Payment terms</div>     <div className="font-medium">Net 30</div>
                  <div className="text-zinc-500">Handling fee</div>      <div className="font-medium">$1.20 / order</div>
                  <div className="text-zinc-500">MAP enforcement</div>   <div className="font-medium">Strict · auto-flagged</div>
                  <div className="text-zinc-500">Returns window</div>    <div className="font-medium">14 days</div>
                  <div className="text-zinc-500">Damaged shipments</div> <div className="font-medium">100% credit + reship if photo &lt; 7d</div>
                </div>
                <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center gap-2">
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-zinc-100 inline-flex items-center gap-1"><Lock className="w-3 h-3" />Locked since Jan 12, 2026</span>
                  <button className="text-xs font-medium text-zinc-900 hover:underline">Propose amendment</button>
                </div>
              </div>
            )}

            {innerTab === "orders" && (
              <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
                {m.orders.filter(o => o.partner === selected.name).length === 0 && (
                  <div className="p-8 text-center text-sm text-zinc-500">No orders for {selected.name} in this window.</div>
                )}
                {m.orders.filter(o => o.partner === selected.name).map((o, i) => (
                  <div key={o.id} className={`px-5 py-3.5 flex items-center gap-3 hover:bg-zinc-50/60 ${i > 0 ? "border-t border-zinc-100" : ""}`}>
                    <span className="font-mono text-[10px] text-zinc-500 w-20">{o.id}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{o.customer}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{o.note}</div>
                    </div>
                    <span className="tabular-nums text-sm font-medium">${o.total.toFixed(2)}</span>
                    <StatusPill status={o.status} />
                    <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                  </div>
                ))}
              </div>
            )}

            {innerTab === "catalog" && (
              <div className="rounded-2xl bg-white border border-zinc-200/80 overflow-hidden">
                <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
                  <div className="text-sm font-semibold">{isSeller ? "Mirrored to this buyer" : "Available from this supplier"}</div>
                  <button className="text-xs font-medium text-zinc-500 hover:text-zinc-900">Manage</button>
                </div>
                {m.catalog.slice(0, 4).map((p, i) => (
                  <div key={p.sku} className={`px-5 py-3 flex items-center gap-3 hover:bg-zinc-50/60 ${i > 0 ? "border-t border-zinc-100" : ""}`}>
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-zinc-100 to-zinc-50 flex-shrink-0 flex items-center justify-center">
                      <Package className="w-3.5 h-3.5 text-zinc-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-zinc-500 font-mono">{p.sku}</div>
                    </div>
                    {isSeller ? (
                      <div className="text-right text-[11px] text-zinc-700">MSRP ${p.msrp} · MAP ${p.map} · WS ${p.wholesale}</div>
                    ) : (
                      <div className="text-right">
                        <div className="text-[11px] text-zinc-700">WS ${p.wholesale} · MSRP ${p.msrp}</div>
                        <div className="text-[11px] font-semibold text-emerald-700">{Math.round((p.margin || 0.6) * 100)}%</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {innerTab === "messages" && (
              <div className="rounded-2xl bg-white border border-zinc-200/80 p-5">
                <div className="space-y-4">
                  <MessageBubble side="them" name={selected.name} text="Any ETA on DS-21041? Customer is asking." time="2h ago" />
                  <MessageBubble side="you" name="You" text="Pulled tracking just now — UPS scan delayed. I'll add manually by 5pm." time="1h ago" />
                </div>
                <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center gap-2">
                  <input placeholder="Reply…" className="flex-1 text-sm px-3 py-2 rounded-md border border-zinc-200 focus:border-zinc-900 outline-none" />
                  <button className="text-xs font-medium px-3 py-2 rounded-md bg-zinc-900 text-white">Send</button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </PortalChrome>
  );
}

function FilterChip({ label, count, active, tone }) {
  const cls = active
    ? "bg-zinc-900 text-white"
    : tone === "amber"
      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
      : "bg-zinc-50 text-zinc-700 ring-1 ring-zinc-200";
  return (
    <button className={`text-[11px] font-medium px-2 py-1 rounded-md ${cls}`}>
      {label} <span className="opacity-70 tabular-nums">{count}</span>
    </button>
  );
}

function ActivityRow({ color, title, desc }) {
  const map = { emerald: "bg-emerald-500", blue: "bg-blue-500", amber: "bg-amber-500", zinc: "bg-zinc-400", red: "bg-red-500" };
  return (
    <div className="flex items-start gap-3">
      <div className={`w-2 h-2 rounded-full ${map[color]} mt-1.5 flex-shrink-0`}></div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{title}</div>
        <div className="text-[11px] text-zinc-500">{desc}</div>
      </div>
    </div>
  );
}

function ActionRow({ tone, title, body }) {
  const cls = tone === "amber" ? "border-amber-200 bg-amber-50" : "border-zinc-200 bg-white";
  return (
    <div className={`rounded-lg border ${cls} p-3 flex items-center gap-3`}>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold">{title}</div>
        <div className="text-[11px] text-zinc-600">{body}</div>
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-zinc-400" />
    </div>
  );
}

function MessageBubble({ side, name, text, time }) {
  if (side === "you") {
    return (
      <div className="flex gap-3 flex-row-reverse">
        <div className="w-8 h-8 rounded-full bg-zinc-900 text-white flex items-center justify-center text-[11px] font-semibold flex-shrink-0">YOU</div>
        <div className="flex-1 max-w-[80%]">
          <div className="bg-zinc-900 text-white rounded-lg px-3 py-2 text-sm">{text}</div>
          <div className="text-[10px] text-zinc-400 mt-1 text-right">{time}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-[11px] font-semibold flex-shrink-0">{name.split(" ").map(w => w[0]).slice(0, 2).join("")}</div>
      <div className="flex-1 max-w-[80%]">
        <div className="text-[10px] font-medium text-zinc-500 mb-0.5">{name}</div>
        <div className="bg-zinc-50 rounded-lg px-3 py-2 text-sm">{text}</div>
        <div className="text-[10px] text-zinc-400 mt-1">{time}</div>
      </div>
    </div>
  );
}
